import React, { useState } from 'react';
import { ref, push } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { database, storage } from '../firebase/config';
import { sendNotificationEmail } from '../services/emailService';
import './ContactForm.css';

const initialState = {
  fullName: '',
  company: '',
  interest: '',
  email: '',
  phone: '',
  message: '',
  documents: null,
  privacyAccepted: false,
};

const ContactForm = () => {
  const [formData, setFormData] = useState(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'file') {
      setFormData((prev) => ({ ...prev, [name]: files }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
  };

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return [];
    
    const uploadPromises = Array.from(files).map(async (file) => {
      try {
        const fileRef = storageRef(storage, `contact-documents/${Date.now()}-${file.name}`);
        const snapshot = await uploadBytes(fileRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        return {
          name: file.name,
          size: file.size,
          type: file.type,
          url: downloadURL
        };
      } catch (error) {
        console.error('Fehler beim Upload der Datei:', file.name, error);
        return null;
      }
    });
    
    const results = await Promise.all(uploadPromises);
    return results.filter(result => result !== null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      // Upload files to Firebase Storage
      const uploadedFiles = await uploadFiles(formData.documents);
      
      // Prepare data for Realtime Database
      const submissionData = {
        fullName: formData.fullName,
        company: formData.company,
        interest: formData.interest,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        documents: uploadedFiles,
        privacyAccepted: formData.privacyAccepted,
        timestamp: new Date().toISOString(),
        submittedAt: Date.now()
      };
      
      // Save to Firebase Realtime Database
      const contactRef = ref(database, 'contact-submissions');
      await push(contactRef, submissionData);
      
      // Send notification email
      const emailResult = await sendNotificationEmail(submissionData, uploadedFiles);
      
      if (emailResult.success) {
        console.log('Benachrichtigungs-E-Mail erfolgreich gesendet');
        // Success - both Firebase and Email successful
        setSubmitStatus('success');
        setFormData(initialState);
      } else {
        console.error('Fehler beim Senden der Benachrichtigungs-E-Mail:', emailResult.error);
        // Firebase successful but email failed
        setSubmitStatus('email-error');
      }
      
    } catch (error) {
      console.error('Fehler beim Absenden des Formulars:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="contact-form" id="contact-form">
      <div className="contact-form__inner">
        <h2 className="contact-form__title">
          Lassen Sie uns<br/>
          <span>gemeinsam gestalten</span>.
        </h2>
        <p className="contact-form__desc">Wir freuen uns auf Ihre Kontaktaufnahme und ein persönliches Gespräch.</p>

        <form className="contact-form__form" onSubmit={handleSubmit}>
          <div className="contact-form__row">
            <label className="contact-form__label" htmlFor="fullName">Vor- und Nachname *</label>
            <input
              id="fullName"
              name="fullName"
              type="text"
              required
              value={formData.fullName}
              onChange={handleChange}
              className="contact-form__input"
              placeholder="Ihr Name"
            />
          </div>

          <div className="contact-form__row">
            <label className="contact-form__label" htmlFor="company">Firma (optional)</label>
            <input
              id="company"
              name="company"
              type="text"
              value={formData.company}
              onChange={handleChange}
              className="contact-form__input"
              placeholder="Firmenname"
            />
          </div>

          <div className="contact-form__row">
            <label className="contact-form__label" htmlFor="interest">Ich bin interessiert an *</label>
            <select
              id="interest"
              name="interest"
              required
              value={formData.interest}
              onChange={handleChange}
              className="contact-form__select"
            >
              <option value="" disabled>Bitte auswählen</option>
              <option>Laufende Betreuung</option>
              <option>Steuerberatung</option>
              <option>Strategieberatung & Gestaltungsberatung</option>
              <option>Betriebswirtschaftliche Beratung</option>
              <option>Kooperation</option>
              <option>Sonstiges</option>
            </select>
          </div>

          <div className="contact-form__row">
            <label className="contact-form__label" htmlFor="email">E-Mail Adresse *</label>
            <input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="contact-form__input"
              placeholder="ihre@email.com"
            />
          </div>

          <div className="contact-form__row">
            <label className="contact-form__label" htmlFor="phone">Telefonnummer *</label>
            <input
              id="phone"
              name="phone"
              type="tel"
              required
              value={formData.phone}
              onChange={handleChange}
              className="contact-form__input"
              placeholder="+43 ..."
            />
          </div>

          <div className="contact-form__row">
            <label className="contact-form__label" htmlFor="message">Weitere Information</label>
            <textarea
              id="message"
              name="message"
              rows={5}
              value={formData.message}
              onChange={handleChange}
              className="contact-form__textarea"
              placeholder="Ihre Nachricht"
            />
          </div>

          <div className="contact-form__row">
            <label className="contact-form__label" htmlFor="documents">Dokumente hochladen</label>
            <div className="contact-form__file-upload">
              <input
                id="documents"
                name="documents"
                type="file"
                multiple
                onChange={handleChange}
                className="contact-form__file-input"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
              />
              <label htmlFor="documents" className="contact-form__file-label">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M17 8L12 3L7 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 3V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="contact-form__file-text">
                  {formData.documents && formData.documents.length > 0 
                    ? `${formData.documents.length} Datei(en) ausgewählt`
                    : 'Dateien auswählen oder hierher ziehen'
                  }
                </span>
              </label>
            </div>
            <p className="contact-form__file-hint">PDF, DOC, XLS, Bilder (max. 10MB pro Datei)</p>
          </div>

          <label className="contact-form__checkbox">
            <input
              type="checkbox"
              name="privacyAccepted"
              checked={formData.privacyAccepted}
              onChange={handleChange}
              required
            />
            <span>Ich akzeptiere die Datenschutzbestimmungen.</span>
          </label>

          <button 
            type="submit" 
            className={`contact-form__submit ${isSubmitting ? 'contact-form__submit--loading' : ''}`}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="contact-form__spinner"></span>
                <span>Wird gesendet...</span>
              </>
            ) : (
              'Anfrage senden'
            )}
          </button>

          {submitStatus === 'success' && (
            <div className="contact-form__success-overlay">
              <div className="contact-form__success-content">
                <div className="contact-form__success-icon">
                  <svg viewBox="0 0 52 52">
                    <circle className="contact-form__success-circle" cx="26" cy="26" r="25" fill="none"/>
                    <path className="contact-form__success-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
                  </svg>
                </div>
                <h3 className="contact-form__success-title">Vielen Dank!</h3>
                <p className="contact-form__success-text">
                  Ihre Anfrage ist bei uns eingegangen.<br/>
                  Wir melden uns in Kürze bei Ihnen.
                </p>
                <button 
                  type="button" 
                  className="contact-form__success-button"
                  onClick={() => setSubmitStatus(null)}
                >
                  Verstanden
                </button>
              </div>
            </div>
          )}
          
          {submitStatus === 'email-error' && (
            <div className="contact-form__message contact-form__message--warning">
              <span className="contact-form__message-icon">⚠️</span>
              <div>
                <strong>Anfrage eingegangen</strong>
                <p>Wir haben Ihre Daten erhalten und melden uns bei Ihnen.</p>
              </div>
            </div>
          )}
          
          {submitStatus === 'error' && (
            <div className="contact-form__message contact-form__message--error">
              <span className="contact-form__message-icon">❌</span>
              <div>
                <strong>Fehler aufgetreten</strong>
                <p>Bitte versuchen Sie es erneut oder kontaktieren Sie uns telefonisch.</p>
              </div>
            </div>
          )}
        </form>
      </div>
    </section>
  );
};

export default ContactForm;


